/**
 * **联机模式下的"一桌"** —— 它把 `S3Peer`、大厅协议、`S3Session` 缝成
 * 界面层能用的一件东西。
 *
 * 界面不该知道 Trystero、不该知道 `makeAction`、也不该知道 peer id 从哪来。
 * 它只想知道三件事：**大厅现在长什么样、我能改什么、开局之后我该看什么**。
 * 这个文件就是那三件事。
 *
 * ## 生命周期
 *
 * ```
 *   进房  connect()  ─────────────────────────────────────►  dispose()
 *            │  S3Peer（随房间）
 *            │  LobbyNet（大厅协商，随房间）
 *            └─ 开局 openGame() → S3Session（随对局）
 *                                   └─ 换一局：再 openGame 一次，Peer 复用
 * ```
 *
 * 这和 `docs/netcode-s3.md` §1 那张图是同一件事：
 * **Peer 随房间、Session 随对局**。
 *
 * ## 对局房主是谁：每个人**独立算**出来的，不协商
 *
 * 评审：「进入游戏后 host 仅由房间中网络最快者担任，和谁创建房间无关」。
 * 所以开局时每个人都跑一遍 `electGameHost(state.players)` —— 它是纯函数，
 * 数据（延迟）也一致，于是**所有人得到同一个答案**。
 * 自己算出来是自己 → `hostSession`；否则 → `clientSession`。
 * 不需要再多一轮"谁来当房主"的协商，也就没有协商失败的窗口。
 */

import { createRegistry, type CardRegistry } from '../core/pack';
import { t } from '../i18n';
import type { RoomConfig } from '../core/config';
import type { GameAction } from '../core/actions';
import type { GameEvent } from '../core/events';
import { createS3Peer, type S3Peer } from '../net/peer';
import { clientSession, hostSession, type S3Session } from '../net/session';
import { createTrysteroTransport, type TurnServerConfig } from '../net/trystero';
import { createSideChannel, type SideChannel } from '../net/side';
import { electHost } from '../net/election';
import { addBot as addLobbyBot, type LobbyState, type PackRef } from '../ui/room';
import { createEkRules, type EkView } from './rules';
import { getRemoteBots, remoteBotUrl } from './remoteBot';
import { createLobbyNet, type LobbyNet } from './lobby';

export interface ConnectRoomParams {
  /** 房间号。玩家念给朋友听的那四个字符 */
  readonly roomId: string;
  /** 应用 id。同一信令网络下隔离不同游戏 */
  readonly appId: string;
  /** 我自己的 peer id（`S3Peer` 会给，这里只是为了日志） */
  readonly selfId?: string;
  readonly registry: CardRegistry;
  /** 当前大厅状态。**每次要发之前现取** */
  readonly snapshot: () => LobbyState | null;
  /** 我这一行 RTT。回合心跳之外，大厅广播也带一份 */
  readonly rttRow: () => Record<string, number>;
  /** 本机装了哪些内容包（id + 内容指纹） */
  readonly localPacks: () => readonly PackRef[];
  /** 本机的完整 git commit hash（进房和房主比对） */
  readonly localCommit: () => string;
  /** 远端改了名册/游戏规则 → 并进本地模型 */
  readonly apply: (next: LobbyState, from: string) => void;
  readonly onKicked: (by: string, reason?: string) => void;
  /** 和房主版本对不上，进不了房 */
  readonly onVersionMismatch: (reason: string) => void;
  /** 创建者宣布开局（含它手里的权威游戏规则）。调用方负责把界面推进对局屏 */
  readonly onStart: (seed: number, host: string | null, config: RoomConfig | null) => void;
  /**
   * 信令中继表。不给就由 `net/trystero.ts` 的 `resolveRelayUrls()` 决定
   * （环境变量 / 查询参数 / localStorage / `DEFAULT_RELAYS`）。
   *
   * ⚠️ **同房两端必须是同一张**。这是"公共中继各连各的、互不相认"的唯一解。
   */
  readonly relayUrls?: readonly string[];
  /**
   * **TURN 服务器**（可选）。STUN 打不通的网络组合（对称 NAT，经验值约 8%）靠它转发。
   * 凭据怎么来见 `docs/guides/cloudflare-turn.md`。不给就只用 STUN。
   */
  readonly turnServers?: readonly TurnServerConfig[];
  /**
   * 信令 / 连接层报错（中继断、ICE 失败……）。**仅供观测** —— 房间不会因为
   * 一次错误就没了（心跳与 `resync` 兜底），但调用方可以据此把「连不上」
   * 翻译成给人看的理由（见 `main.ts` 的 `startConnect`）。
   */
  readonly onError?: (error: unknown) => void;
}

export interface OnlineRoom {
  readonly peer: S3Peer;
  readonly lobby: LobbyNet;
  /**
   * **旁路通道**：不进状态、不经房主的实时数据（抽牌对峙的拖拽帧与 hover）。
   * 它建在 `peer` 上，所以随房间活着 —— 换一局不用重建。
   */
  readonly side: SideChannel;
  readonly selfId: string;
  /** 我的延迟（毫秒）。选对局房主要用它 */
  rtt(): number | null;
  /** 名册变化 / 延迟变化之后重喊一次 */
  announce(): void;
  /** **手动刷新（大厅）**：让别人立刻重播 presence，创建者补发权威配置 */
  refresh(): void;
  /**
   * **手动同步（对局）**：客户端重取全量 / 房主重推全量，顺手也刷新一次大厅。
   * 没有对局时只刷新大厅。用来从「卡死」里脱身，不重置对局。
   */
  sync(): Promise<void>;
  /**
   * **选出对局房主。**纯函数 + 共享矩阵，所以所有人算出同一个人。
   *
   * 创建者开局时会先问一次，把结果随 `lobby/start` 一起发出去 ——
   * 于是即使某个人的矩阵还没拼全（刚进房、少收了一行），
   * 他也照创建者说的那个人走，不会出现"两个人都认为自己是房主"。
   */
  pickGameHost(): string | null;
  /**
   * **创建者：开局。** 广播给别人，**自己也开一局**。
   *
   * ⚠️ 第一版只广播、自己不开，于是创建者那一屏去问
   * `session()` 时拿到的还是 `null`，一路退回热座 ——
   * 而热座拿**昵称**当席位 id，两个默认昵称一样的人一撞就开不出局。
   * 症状是"两个人都进了对局屏，但房主那边报开局失败"。
   */
  startGame(seed: number): string | null;
  /** 只有创建者该调它 */
  broadcastConfig(state: LobbyState): void;
  /** 只有创建者该调它 */
  kick(target: string): void;
  /** 创建者：加一个机器人席位（无 peer，进对局后由房主代打）。返回 id，失败 null */
  addBot(): string | null;
  /**
   * 开局。**每个人各自调一次**（创建者先广播 `lobby/start`）。
   * 返回值告诉你自己是不是对局房主。
   */
  openGame(
    seed: number,
    announcedHost?: string | null,
    config?: RoomConfig | null,
  ): S3Session<EkView, GameAction, GameEvent> | null;
  /** 当前那一局。没有就是 null */
  session(): S3Session<EkView, GameAction, GameEvent> | null;
  /** 换一局：把上一局收掉，Peer 留着 */
  closeGame(): void;
  dispose(): void;
}

export function connectRoom(params: ConnectRoomParams): OnlineRoom {
  const transport = createTrysteroTransport({
    roomId: params.roomId,
    appId: params.appId,
    ...(params.relayUrls === undefined ? {} : { relayUrls: params.relayUrls }),
    ...(params.turnServers === undefined ? {} : { turnServers: [...params.turnServers] }),
    onError: (error) => {
      // 信令层的抖动不该把房间弄没 —— 心跳与 `resync` 会兜底；
      // 调用方还可以用它把「连不上」翻译成人话（见 `main.ts` 的 `startConnect`）
      params.onError?.(error);
    },
  });
  const peer = createS3Peer({ transport, onError: () => {} });

  const lobby = createLobbyNet({
    channel: peer,
    selfId: peer.selfId,
    snapshot: params.snapshot,
    rttRow: params.rttRow,
    localPacks: params.localPacks,
    localCommit: params.localCommit,
    apply: params.apply,
    onKicked: params.onKicked,
    onVersionMismatch: params.onVersionMismatch,
    onStart: params.onStart,
  });
  lobby.start();

  const side = createSideChannel(peer);
  let current: S3Session<EkView, GameAction, GameEvent> | null = null;

  const room: OnlineRoom = {
    peer,
    lobby,
    side,
    selfId: peer.selfId,

    rtt() {
      // 房间里所有人都测过 —— 选举要的是"我对所有人"那一份，
      // 但房间期只跟创建者通信，所以取一个就够了
      const others = peer.peers();
      let best: number | null = null;
      for (const id of others) {
        const value = peer.rtt(id);
        if (value === null) continue;
        best = best === null ? value : Math.min(best, value);
      }
      return best;
    },

    announce() {
      lobby.announce();
    },

    refresh() {
      lobby.refresh();
    },

    async sync() {
      // 大厅也刷新一次：房间页按它时还没有对局；对局中它也无害
      lobby.refresh();
      if (current !== null) await current.sync();
    },

    pickGameHost() {
      const state = params.snapshot();
      if (state === null) return null;
      // ⚠️ 用**矩阵**版（`net/election.ts`），不是大厅那个一维版。
      //    一维版吃的是"每个玩家到房间的延迟"，而那件事在联机里
      //    只有本人知道 —— 每个人拿到的表里只有自己那格是实数，
      //    于是**每个人都算出自己是房主**（两个对局各开各的）。
      //    矩阵版要的是所有人上报的行，拼起来才是共享的事实
      return electHost({
        players: state.players.map((player) => player.id),
        matrix: lobby.rttMatrix(),
      });
    },

    startGame(seed) {
      const host = room.pickGameHost();
      // 创建者手里的游戏规则是权威 —— 随 START 一起发，后进房的人（可能是房主）
      // 也能拿到"改过的牌堆构成"，不会用默认牌堆开局。
      const config = params.snapshot()?.config ?? null;
      // 先把"谁是对局房主"定下来再广播 —— 别人照它走，不会各开一局
      lobby.startGame(seed, host, config);
      room.openGame(seed, host, config);
      return host;
    },

    broadcastConfig(state) {
      lobby.broadcastConfig(state.config);
    },

    kick(target) {
      lobby.kick(target);
    },

    addBot() {
      const state = params.snapshot();
      if (state === null) return null;
      const result = addLobbyBot(state, params.registry);
      if (!result.ok) return null;
      // 本地先落，再广播给其他 peer
      params.apply(result.value, params.selfId ?? '');
      const bot = result.value.players[result.value.players.length - 1];
      if (bot === undefined) return null;
      lobby.addBot(bot.id, bot.nickname);
      return bot.id;
    },

    openGame(seed, announcedHost, config) {
      room.closeGame();
      const state = params.snapshot();
      if (state === null) return null;

      // 创建者宣布的那个人优先；没宣布就自己按矩阵算一遍
      const hostId = announcedHost ?? room.pickGameHost() ?? state.players[0]?.id ?? null;
      if (hostId === null) return null;

      const names: Record<string, string> = {};
      const bots: string[] = [];
      for (const player of state.players) {
        names[player.id] = player.nickname;
        if (player.isBot === true) bots.push(player.id);
      }

      // 配好 MCP（?mcp=1 / __ROULETTE_MCP_URL__）时，把**第一个** bot 座位
      // 交给远程 LLM，其余仍是本地启发式。问不到就自动回退，不会拖死整桌。
      const remoteBotIds = remoteBotUrl() === null ? [] : bots.slice(0, 1);
      // ★ 创建者随 START 发来的游戏规则优先（后进房的房主本地那份可能是默认的）
      const startOptions = { config: config ?? state.config, seed, names, botIds: bots, remoteBotIds };

      // ★ 用**房间启用的包**重建注册表 —— 不是拿本机全部包去开。
      //    少一个包 / 多一个包都会让 `validateConfig` 的 packs/mismatch 拒掉开局，
      //    所以这一步是"所有人加载内容一致"真正落地的地方。
      const enabledRegistry = createRegistry(
        params.registry.packs.filter((pack) => state.config.enabledPacks.includes(pack.id)),
      );
      const rules = createEkRules(enabledRegistry, getRemoteBots());

      current =
        hostId === peer.selfId
          ? hostSession(peer, rules, {
              players: state.players.map((player) => player.id),
              options: startOptions,
              bots,
            })
          : clientSession(peer, rules, {});
      return current;
    },

    session: () => current,

    closeGame() {
      current?.close(t('host.online.nextGame'));
      current = null;
    },

    dispose() {
      room.closeGame();
      lobby.dispose();
      peer.destroy();
    },
  };

  return room;
}
